import type { BoardData, Item, StandHolder, StandSpec } from '../model/schema.js';

/** A real holder (not `auto` / `none`). */
export type HolderType = Exclude<StandHolder, 'auto' | 'none'>;

const HOLDER_LABEL: Record<'en' | 'de', Record<HolderType, string>> = {
  en: { stereo_bar: 'Stereo bar', mic_bar: 'Mic bar', double_clamp: 'Double clamp' },
  de: { stereo_bar: 'Stereoschiene', mic_bar: 'Mikrofonschiene', double_clamp: 'Doppelklemme' },
};

/** Holder name for lists ("Stereo bar"). */
export function holderLabel(type: HolderType, locale: 'en' | 'de' = 'en'): string {
  return HOLDER_LABEL[locale][type];
}

/** The holder setting of a stand spec: `holder`, else `stereo_bar` for the old switch, else auto. */
export function holderSetting(spec: StandSpec | undefined): StandHolder {
  return spec?.holder ?? (spec?.stereoBar === true ? 'stereo_bar' : 'auto');
}

/** Mics / DIs mounted on a stand. */
export function mountedMics(data: BoardData, standId: string): Item[] {
  return Object.values(data.items).filter(
    (i) => i.mountedOn === standId && (i.kind === 'microphone' || i.kind === 'di'),
  );
}

/**
 * Holder a stand needs (U70): a chosen holder always; automatic = a stereo bar for two mics, a mic
 * bar for three or more, nothing for one; `none` = the mics are fixed separately.
 */
export function standHolder(data: BoardData, stand: Item): HolderType | null {
  const setting = holderSetting(stand.stand);
  if (setting === 'none') return null;
  if (setting !== 'auto') return setting;
  const n = mountedMics(data, stand.id).length;
  return n >= 3 ? 'mic_bar' : n === 2 ? 'stereo_bar' : null;
}
